import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MobileNavigation } from '../components/MobileNavigation';
import { 
  Basketball,
  Users,
  UsersFour,
  Warning,
  Phone,
  EnvelopeSimple,
  FirstAid,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Scroll,
  ShieldCheck,
  HandPalm
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/api';

export function ProgramRules() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('parent');
  const [activeTab, setActiveTab] = useState('game');
  
  useEffect(() => {
    fetchUserRole();
  }, []);
  
  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role || 'parent');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };
  
  const isStaffOrAdmin = userRole === 'staff' || userRole === 'admin';
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="program-rules-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft size={24} />
            </Button>
            <div>
              <h1 className="text-xl font-heading flex items-center gap-2">
                <Scroll size={24} className="text-primary" />
                PROGRAM RULES
              </h1>
              <p className="text-sm text-muted-foreground">Stay On Hoops Official Rules</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'game' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('game')}
            className="whitespace-nowrap"
            data-testid="tab-game"
          >
            <Basketball size={16} className="mr-1" />
            Game Rules
          </Button>
          {isStaffOrAdmin && (
            <Button
              variant={activeTab === 'staff' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('staff')}
              className="whitespace-nowrap"
              data-testid="tab-staff"
            >
              <ShieldCheck size={16} className="mr-1" />
              Staff Rules
            </Button>
          )}
          <Button
            variant={activeTab === 'parent' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('parent')}
            className="whitespace-nowrap"
            data-testid="tab-parent"
          >
            <UsersFour size={16} className="mr-1" />
            Parent Rules
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 pb-6 space-y-4">
        
        {/* ==================== GAME RULES ==================== */}
        {activeTab === 'game' && (
          <div className="space-y-4" data-testid="game-rules-content">
            {/* Header Card */}
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <Basketball size={48} className="mx-auto text-primary mb-2" weight="duotone" />
                  <h2 className="text-xl font-heading text-primary">GAME RULES (Players)</h2>
                  <p className="text-sm text-muted-foreground mt-1">Ages 9–14 • Stay On Hoops</p>
                </div>
              </CardContent>
            </Card>
            
            {/* How It Works */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <CheckCircle size={20} className="text-green-500" />
                  HOW IT WORKS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Players must check in on arrival (QR or staff).</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Teams are formed on site.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Games run continuously throughout the session.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Winning team stays on the court.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Losing team goes to the bottom of the "Next Up" list.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Waiting is part of the program — be ready when your name is called.</p>
              </CardContent>
            </Card>
            
            {/* Game Format */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Basketball size={20} className="text-primary" />
                  GAME FORMAT
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-primary/10 rounded-lg p-3 border border-primary/30">
                  <p className="font-heading text-primary text-sm">FIRST GAME</p>
                  <p className="text-lg font-bold">Game to 13</p>
                  <p className="text-sm text-muted-foreground">2s and 1s</p>
                </div>
                <div className="bg-muted/20 rounded-lg p-3 border border-border">
                  <p className="font-heading text-sm">ALL GAMES AFTER</p>
                  <p className="text-lg font-bold">Game to 11</p>
                  <p className="text-sm text-muted-foreground">1s only</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Timed Games */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Clock size={20} className="text-yellow-500" />
                  TIMED GAMES
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Each game has a time cap based on session length.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>If a team reaches the target score before time expires → game ends immediately.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>If time expires before the target score:</p>
                <div className="ml-6 space-y-1 text-muted-foreground">
                  <p>– Highest score wins</p>
                  <p>– If tied → first basket wins</p>
                  <p>– No overtime</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Fouls */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <HandPalm size={20} className="text-yellow-500" />
                  FOULS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Defense calls fouls.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Obvious fouls are respected.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>No arguing obvious calls.</p>
                <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/30 mt-3">
                  <p className="font-heading text-yellow-500 text-sm mb-2">QUESTIONABLE FOULS</p>
                  <p className="text-sm">If there is disagreement:</p>
                  <ul className="text-sm mt-1 space-y-1 ml-4">
                    <li>• Players shoot for it</li>
                    <li>• Make = foul stands</li>
                    <li>• Miss = play continues</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2">This keeps the run moving.</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Player Expectations */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Users size={20} className="text-blue-500" />
                  PLAYER EXPECTATIONS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Players are expected to:</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Respect teammates and opponents</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Play hard but controlled</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Handle wins and losses properly</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Be ready when called</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Follow staff direction at all times</p>
              </CardContent>
            </Card>
            
            {/* Removal From Run */}
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2 text-red-500">
                  <XCircle size={20} />
                  REMOVAL FROM RUN
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>A player may be removed for:</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Dangerous play</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Fighting or aggressive behavior</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Repeated disrespect</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Ignoring staff direction</p>
                <p className="text-xs text-red-400 mt-2">Removal may result in forfeited session credit.</p>
              </CardContent>
            </Card>
            
            {/* Core Principle */}
            <Card className="bg-primary/20 border-primary/50">
              <CardContent className="pt-4 text-center">
                <p className="font-heading text-lg text-primary mb-2">CORE PRINCIPLE</p>
                <p className="text-xl font-bold">Win and Stay On.</p>
                <p className="text-xl font-bold">Lose and Wait.</p>
                <p className="text-xl font-bold text-primary">The Game Decides.</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* ==================== STAFF RULES ==================== */}
        {activeTab === 'staff' && isStaffOrAdmin && (
          <div className="space-y-4" data-testid="staff-rules-content">
            {/* Header Card */}
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <ShieldCheck size={48} className="mx-auto text-blue-500 mb-2" weight="duotone" />
                  <h2 className="text-xl font-heading text-blue-500">STAFF RULES & ENFORCEMENT</h2>
                  <p className="text-sm text-muted-foreground mt-1">Keep it moving. Keep it safe. Let the game teach.</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Staff Role */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">STAFF ROLE</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Staff are present for:</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Safety</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Game flow</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Rotation structure</p>
                <p className="text-yellow-500 font-medium mt-2">Staff do not coach or referee.</p>
              </CardContent>
            </Card>
            
            {/* Check-In Responsibilities */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <CheckCircle size={20} className="text-green-500" />
                  CHECK-IN RESPONSIBILITIES
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Verify waiver status</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Check players in via QR/admin</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Direct players to waiting area</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Enforce capacity limits</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Late arrivals join the next rotation</p>
              </CardContent>
            </Card>
            
            {/* Game Flow */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Basketball size={20} className="text-primary" />
                  GAME FLOW
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>First game to 13 (2s & 1s)</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Games after to 11 (1s only)</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Timed games enforced</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Winners stay on</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Losing team goes to bottom</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>No mid-game substitutions</p>
                <p className="bg-yellow-500/10 p-2 rounded mt-2 text-yellow-500">Staff control the clock and give a loud 30-second warning before time expires.</p>
              </CardContent>
            </Card>
            
            {/* Foul Management */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">FOUL MANAGEMENT</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Defense calls fouls</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Questionable fouls → shoot for it</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Staff intervene only if arguments escalate</p>
              </CardContent>
            </Card>
            
            {/* Safety */}
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2 text-red-500">
                  <Warning size={20} />
                  SAFETY (IMMEDIATE STOP)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Stop play for:</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Dangerous contact</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Aggressive behavior</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Fighting</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Repeated disrespect</p>
              </CardContent>
            </Card>
            
            {/* Player Removal */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <XCircle size={20} className="text-red-500" />
                  PLAYER REMOVAL
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Remove players for:</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Unsafe play</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Continued arguing</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Disrespect toward others</p>
                <p className="flex items-start gap-2"><span className="text-red-500">•</span>Ignoring staff direction</p>
                <p className="bg-yellow-500/10 p-2 rounded mt-2 text-yellow-500 text-xs">Log all removals or serious incidents in admin notes.</p>
              </CardContent>
            </Card>
            
            {/* Drop-off / Pick-up */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">DROP-OFF / PICK-UP ENFORCEMENT</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Program operates as drop-off and pick-up only, unless the facility allows parents inside.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Parents may enter briefly to check in their child, then exit.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Parents return at session end for pickup.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>No sideline coaching.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Ensure players are released only to parents/guardians.</p>
                <p className="bg-red-500/10 p-2 rounded mt-2 text-red-400 text-xs">Do not leave minors unattended — contact parents if late.</p>
              </CardContent>
            </Card>
            
            {/* Credits & Attendance */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">CREDITS & ATTENDANCE</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>No refunds.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Cancel at least 3 hours before session to receive credit.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Late cancellations and no-shows forfeit the fee.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Credits are non-transferable.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Document incidents and removals.</p>
              </CardContent>
            </Card>
            
            {/* Emergencies */}
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2 text-red-500">
                  <FirstAid size={20} />
                  EMERGENCIES
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-bold text-red-500">Follow the Emergency Action Plan immediately.</p>
                <Button 
                  variant="outline" 
                  className="mt-3 w-full border-red-500/30 text-red-500 hover:bg-red-500/10"
                  onClick={() => navigate('/staff/resources')}
                >
                  <FirstAid size={18} className="mr-2" />
                  View Emergency Action Plan
                </Button>
              </CardContent>
            </Card>
            
            {/* Core Staff Principle */}
            <Card className="bg-blue-500/20 border-blue-500/50">
              <CardContent className="pt-4 text-center">
                <p className="font-heading text-lg text-blue-500 mb-2">CORE STAFF PRINCIPLE</p>
                <p className="text-xl font-bold">Keep it moving.</p>
                <p className="text-xl font-bold">Keep it safe.</p>
                <p className="text-xl font-bold text-blue-500">Let the game teach.</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* ==================== PARENT RULES ==================== */}
        {activeTab === 'parent' && (
          <div className="space-y-4" data-testid="parent-rules-content">
            {/* Header Card */}
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="pt-4">
                <div className="text-center">
                  <UsersFour size={48} className="mx-auto text-green-500 mb-2" weight="duotone" />
                  <h2 className="text-xl font-heading text-green-500">PARENT RULES</h2>
                  <p className="text-sm text-muted-foreground mt-1">Guidelines for Parents & Guardians</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Drop-off & Pick-up */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">DROP-OFF & PICK-UP</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Stay On Hoops is a drop-off and pick-up program, unless the facility allows spectators.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Parents may enter briefly to check in, then exit.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Parents return at the end of the session for pickup.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>If spectators are allowed, parents must remain in designated areas.</p>
                <p className="bg-yellow-500/10 p-2 rounded mt-2 text-yellow-500 text-xs">Players must be picked up promptly. Stay On Hoops is not responsible for players left after session end.</p>
              </CardContent>
            </Card>
            
            {/* Sideline Expectations */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">SIDELINE EXPECTATIONS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>No sideline coaching</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Allow players to solve problems on their own</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Direct concerns to staff calmly</p>
              </CardContent>
            </Card>
            
            {/* Game Play */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Basketball size={20} className="text-primary" />
                  GAME PLAY
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Games are timed.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Winners stay on, losing teams rotate.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Defense calls fouls.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Questionable calls are settled by shoot-for-it.</p>
              </CardContent>
            </Card>
            
            {/* Cancellations & Credits */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading">CANCELLATIONS & CREDITS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="flex items-start gap-2"><span className="text-primary">•</span>No cash refunds.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Cancel at least 3 hours before session to receive account credit.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Late cancellations and no-shows forfeit the session fee.</p>
                <p className="flex items-start gap-2"><span className="text-primary">•</span>Credits are non-transferable and must be used within the stated timeframe.</p>
              </CardContent>
            </Card>
            
            {/* Conduct */}
            <Card className="bg-yellow-500/10 border-yellow-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2 text-yellow-500">
                  <Warning size={20} />
                  CONDUCT
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Players may be removed for unsafe or disrespectful behavior. Removal may result in forfeited session credit.</p>
              </CardContent>
            </Card>
            
            {/* Parent Notification */}
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2 text-blue-500">
                  <Phone size={20} />
                  PARENT NOTIFICATION
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>If any issue arises involving your child — including injury, behavioral concerns, or safety matters — a Stay On Hoops staff member will notify the parent or guardian immediately using the contact information on file.</p>
                <p className="text-blue-400 font-medium mt-2">Please ensure your contact details are always up to date.</p>
              </CardContent>
            </Card>
            
            {/* Emergency Contact Priority */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Phone size={20} className="text-primary" />
                  EMERGENCY CONTACT PRIORITY ORDER
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="mb-2">Staff will attempt contact in this order:</p>
                <ol className="space-y-2">
                  <li className="flex items-center gap-2 bg-muted/20 p-2 rounded">
                    <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    Phone call
                  </li>
                  <li className="flex items-center gap-2 bg-muted/20 p-2 rounded">
                    <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    Text message
                  </li>
                  <li className="flex items-center gap-2 bg-muted/20 p-2 rounded">
                    <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    Email
                  </li>
                </ol>
                <p className="text-xs text-muted-foreground mt-2">If unreachable, staff will proceed to the secondary emergency contact.</p>
              </CardContent>
            </Card>
            
            {/* Injury Protocol */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <FirstAid size={20} className="text-red-500" />
                  MINOR vs MAJOR INJURY PROTOCOL
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* Minor */}
                <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/30">
                  <p className="font-heading text-yellow-500 text-sm mb-2">MINOR INJURIES</p>
                  <p className="text-xs text-muted-foreground mb-2">(small bumps, light soreness, minor scrapes)</p>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2"><span className="text-yellow-500">•</span>Basic first aid provided</li>
                    <li className="flex items-start gap-2"><span className="text-yellow-500">•</span>Parent notified at pickup or by message</li>
                    <li className="flex items-start gap-2"><span className="text-yellow-500">•</span>Player may return to play if comfortable and cleared by staff</li>
                  </ul>
                </div>
                
                {/* Major */}
                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                  <p className="font-heading text-red-500 text-sm mb-2">MAJOR INJURIES OR SAFETY CONCERNS</p>
                  <p className="text-xs text-muted-foreground mb-2">(head impact, significant pain, suspected sprain/fracture, illness, emotional distress)</p>
                  <ul className="space-y-1">
                    <li className="flex items-start gap-2"><span className="text-red-500">•</span>Play stopped immediately</li>
                    <li className="flex items-start gap-2"><span className="text-red-500">•</span>Parent contacted right away</li>
                    <li className="flex items-start gap-2"><span className="text-red-500">•</span>911 called first if needed</li>
                    <li className="flex items-start gap-2"><span className="text-red-500">•</span>Player will not return to play</li>
                    <li className="flex items-start gap-2"><span className="text-red-500">•</span>Incident documented</li>
                  </ul>
                </div>
                
                <p className="text-center font-bold text-primary">Player safety always comes before gameplay.</p>
              </CardContent>
            </Card>
            
            {/* Questions */}
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="pt-4 text-center">
                <p className="font-heading text-primary mb-2">QUESTIONS?</p>
                <a href="mailto:info@stayonhoops.com" className="flex items-center justify-center gap-2 text-lg hover:underline">
                  <EnvelopeSimple size={24} />
                  info@stayonhoops.com
                </a>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      <MobileNavigation userRole={userRole} />
    </div>
  );
}
